import { Task, LogEntry } from '../../../types';
import { cleanPdfText } from '../../../utils/pdfSanitization';
import { diffDays } from '../../../utils/dateUtils';

export async function runDownloadPDF(
  projectName: string,
  tasks: Task[],
  addLogEntry: (action: LogEntry['action'], description: string) => void
) {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF();
  const pName = projectName || 'Projekt Planung';

  doc.setFontSize(22);
  doc.setTextColor(63, 81, 181);
  doc.text(pName, 14, 22);

  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Exportiert am: ${new Date().toLocaleDateString('de-DE')}`, 14, 30);

  const tableColumn = ['Aufgabe', 'Start', 'Ende', 'Dauer (Tage)', 'Zuständigkeit', 'Status'];
  const tableRows = tasks.map(task => [
    task.name,
    task.start || '-',
    task.end || '-',
    diffDays(task.start, task.end).toString(),
    task.assignee || '-',
    task.completed ? 'Erledigt' : 'Offen',
  ]);

  autoTable(doc, {
    head: [tableColumn],
    body: tableRows,
    startY: 36,
    theme: 'grid',
    headStyles: { fillColor: [63, 81, 181] },
    styles: { fontSize: 9 },
  });

  doc.save(`${pName.replace(/\s+/g, '_')}_Planung.pdf`);
  addLogEntry('import', `PDF für "${pName}" heruntergeladen`);
}

export async function runDownloadRoadmap(
  projectName: string,
  tasks: Task[],
  addLogEntry: (action: LogEntry['action'], description: string) => void,
  options: {
    includeMilestones?: boolean;
    includePhases?: boolean;
    includeMeetings?: boolean;
    includeTasks?: boolean;
    includeSubtasks?: boolean;
    showTodayLine?: boolean;
    startDate?: string;
    endDate?: string;
    minPriority?: string;
    onlyCompleted?: boolean;
    hideCompleted?: boolean;
    projectName?: string;
    tasks?: Task[];
    selectedTypes?: string[];
  } = {}
) {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  const {
    includeMilestones = true,
    includePhases = true,
    includeMeetings = false,
    includeTasks = true,
    includeSubtasks = false,
    showTodayLine = true,
    startDate: customStart,
    endDate: customEnd,
    minPriority = 'all',
    onlyCompleted = false,
    hideCompleted = false,
    projectName: optProjectName,
    tasks: optTasks,
  } = options;
  const pName = cleanPdfText(optProjectName || projectName || 'Projekt Roadmap');
  const sourceTasks = optTasks || tasks;

  const matchesPriority = (p?: string) => {
    if (minPriority === 'all') return true;
    if (minPriority === 'high') return p === 'high';
    if (minPriority === 'medium') return p === 'high' || p === 'medium';
    return true;
  };

  const roadmapTasks = sourceTasks.filter(t => {
    if (t.type === 'contact' || t.category === 'Kommunikation') return false;
    if (onlyCompleted && !(t.completed || t.status === 'completed')) return false;
    if (hideCompleted && (t.completed || t.status === 'completed')) return false;
    if (!includeSubtasks && t.parentId) return false;

    const plottedDate = t.end || t.start || '';
    if (customStart && plottedDate && plottedDate < customStart) return false;
    if (customEnd && plottedDate && plottedDate > customEnd) return false;
    if (!matchesPriority(t.priority)) return false;

    const type = t.type || 'task';
    const selectedTypes = options.selectedTypes;
    if (selectedTypes && selectedTypes.length > 0) {
      if (!selectedTypes.includes(type)) return false;
      if (type === 'task') {
        const isPhase = diffDays(t.start, t.end) > 7;
        if (isPhase && !includePhases) return false;
        if (!isPhase && !includeTasks) return false;
      }
      return true;
    }

    if (type === 'milestone') return includeMilestones;
    if (type === 'meeting') return includeMeetings;
    if (type === 'task') {
      const isPhase = diffDays(t.start, t.end) > 7;
      if (isPhase) return includePhases;
      return includeTasks;
    }
    return false;
  });

  const seenKeys = new Set<string>();
  const uniqueTasks = roadmapTasks.filter(t => {
    if (!t.id) return false;
    const cleanName = cleanPdfText(t.name);
    if (!cleanName) return false;
    const dateKey = `${cleanName}_${t.end || t.start}`;
    if (seenKeys.has(dateKey)) return false;
    seenKeys.add(dateKey);
    return true;
  });

  const getSafeTimestamp = (d: string | number | Date | undefined | null) => {
    if (!d) return null;
    const dateObj = new Date(d);
    const ts = dateObj.getTime();
    if (isNaN(ts)) return null;
    const year = dateObj.getFullYear();
    if (year < 2000 || year > 2100) return null;
    return ts;
  };

  const parsedCustomStart = customStart ? getSafeTimestamp(customStart) : null;
  const parsedCustomEnd = customEnd ? getSafeTimestamp(customEnd) : null;

  let minTime: number;
  let maxTime: number;

  if (parsedCustomStart && parsedCustomEnd) {
    minTime = parsedCustomStart;
    maxTime = parsedCustomEnd;
  } else {
    const itemTimes = uniqueTasks.map(t => getSafeTimestamp(t.end || t.start)).filter(t => t !== null) as number[];
    if (itemTimes.length === 0) {
      minTime = parsedCustomStart || Date.now();
      maxTime = parsedCustomEnd || minTime + 86400000 * 90;
    } else {
      const rawMin = Math.min(...itemTimes);
      const rawMax = Math.max(...itemTimes);
      minTime = parsedCustomStart !== null ? parsedCustomStart : rawMin;
      maxTime = parsedCustomEnd !== null ? parsedCustomEnd : rawMax;
    }
  }

  const duration = Math.max(1, maxTime - minTime);
  const pageWidth = 297;
  const pageHeight = 210;
  const margin = 20;
  const axisWidth = pageWidth - margin * 2;
  const axisY = 105;

  doc.setFillColor(248, 250, 252);
  doc.rect(0, 0, pageWidth, 24, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.3);
  doc.line(0, 24, pageWidth, 24);

  doc.setTextColor(30, 41, 59);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');

  const dateRangeStr = `${new Date(minTime).toLocaleDateString('de-DE')} - ${new Date(maxTime).toLocaleDateString('de-DE')}`;
  const headerTitle = `${pName.toUpperCase()}  |  STRATEGISCHE ROADMAP`;
  doc.text(headerTitle, margin, 15);

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text(`${dateRangeStr}  |  Generiert am ${new Date().toLocaleDateString('de-DE')}`, pageWidth - margin, 15, { align: 'right' });

  doc.setDrawColor(148, 163, 184);
  doc.setLineWidth(0.8);
  doc.line(margin, axisY, pageWidth - margin, axisY);

  const todayStrVal = new Date().toISOString().split('T')[0];
  const todayTs = getSafeTimestamp(todayStrVal);
  const tsSet = new Set(uniqueTasks.map(t => getSafeTimestamp(t.end || t.start)).filter(t => t !== null) as number[]);
  if (minTime !== null) tsSet.add(minTime);
  if (maxTime !== null) tsSet.add(maxTime);
  if (showTodayLine && todayTs !== null && todayTs >= minTime && todayTs <= maxTime) {
    tsSet.add(todayTs);
  }
  const sortedTs = Array.from(tsSet).sort((a, b) => a - b);

  const timeToPos = (t: number) => {
    if (sortedTs.length < 2) return (t - minTime) / duration;
    if (t <= sortedTs[0]) return 0;
    if (t >= sortedTs[sortedTs.length - 1]) return 1;

    const gaps: number[] = [];
    for (let i = 1; i < sortedTs.length; i++) gaps.push(sortedTs[i] - sortedTs[i - 1]);
    const compressedGaps = gaps.map(g => Math.pow(g, 0.22));
    const totalCompressed = compressedGaps.reduce((a, b) => a + b, 0);

    let accumulated = 0;
    for (let i = 0; i < gaps.length; i++) {
      if (t >= sortedTs[i] && t <= sortedTs[i + 1]) {
        const localRatio = (t - sortedTs[i]) / gaps[i];
        return (accumulated + localRatio * compressedGaps[i]) / totalCompressed;
      }
      accumulated += compressedGaps[i];
    }
    return 1;
  };

  if (showTodayLine && todayTs !== null && todayTs >= minTime && todayTs <= maxTime) {
    const todayX = margin + timeToPos(todayTs) * axisWidth;
    doc.setDrawColor(239, 68, 68);
    doc.setLineWidth(0.4);
    doc.setLineDashPattern([2, 2], 0);
    doc.line(todayX, 26, todayX, pageHeight - 20);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(239, 68, 68);
    doc.text('HEUTE', todayX, 29, { align: 'center' });
    doc.setLineDashPattern([], 0);
  }

  if (uniqueTasks.length === 0) {
    doc.setFontSize(10);
    doc.setTextColor(148, 163, 184);
    doc.setFont('helvetica', 'normal');
    doc.text('Keine Meilensteine oder Aufgaben im gewählten Zeitraum', pageWidth / 2, axisY - 10, { align: 'center' });
  } else {
    const sortedItems = [...uniqueTasks]
      .sort((a, b) => (a.end || a.start || '').localeCompare(b.end || b.start || ''))
      .slice(0, 24);

    const assigned = sortedItems
      .map((t, i) => {
        const tTs = getSafeTimestamp(t.end || t.start);
        if (tTs === null) return null;
        const leftRatio = timeToPos(tTs);
        const leftPercent = Math.max(0.06, Math.min(0.94, leftRatio));
        const itemX = margin + leftPercent * axisWidth;
        return { item: t, itemTime: tTs, leftPercent, itemX, index: i };
      })
      .filter(Boolean) as { item: Task; itemTime: number; leftPercent: number; itemX: number; index: number }[];

    let lastSide: 'up' | 'down' = 'down';
    let lastLeft = -1;
    const itemsWithSide = assigned.map(entry => {
      let side: 'up' | 'down';
      if (entry.leftPercent - lastLeft < 0.07) {
        side = lastSide === 'up' ? 'down' : 'up';
      } else {
        side = entry.index % 2 === 0 ? 'up' : 'down';
      }
      lastSide = side;
      lastLeft = entry.leftPercent;
      return { ...entry, side };
    });

    const maxLevels = 4;
    const occupiedSpaces: { cardStart: number; cardEnd: number; level: number; side: 'up' | 'down'; stemX: number }[] = [];

    itemsWithSide.forEach(entry => {
      const { item, itemX, side } = entry;
      const isTop = side === 'up';
      const isMilestone = item.type === 'milestone';

      const cleanName = cleanPdfText(item.name);
      const labelText = cleanName.length > 36 ? cleanName.substring(0, 34) + '...' : cleanName;

      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'bold');
      const textWidth = doc.getTextWidth(labelText);

      const dateStr = (item.end || item.start || '').split('-').reverse().join('.');
      const typeStr = isMilestone ? 'MEILENSTEIN' : 'AUFGABE';
      doc.setFontSize(5);
      const metaWidth = doc.getTextWidth(`${dateStr}   ${typeStr}`);

      const cardWidth = Math.min(58, Math.max(textWidth, metaWidth) + 6);
      const cardHeight = 8.5;

      let candidates: ('left' | 'right')[];
      if (itemX < margin + 45) {
        candidates = ['left'];
      } else if (itemX > pageWidth - margin - 45) {
        candidates = ['right'];
      } else {
        const prevOnSide = occupiedSpaces.filter(s => s.side === side).pop();
        if (prevOnSide && prevOnSide.cardEnd > itemX) {
          candidates = ['left', 'right'];
        } else {
          candidates = ['right', 'left'];
        }
      }

      let bestLevel = 0;
      let bestAlign = candidates[0];
      let foundSlot = false;

      for (let lvl = 0; lvl < maxLevels; lvl++) {
        for (const align of candidates) {
          const cardStart = align === 'left' ? itemX + 2 : itemX - 2 - cardWidth;
          const cardEnd = cardStart + cardWidth;

          const cardCollision = occupiedSpaces.some(
            s => s.side === side && s.level === lvl && !(cardEnd + 3 < s.cardStart || cardStart - 3 > s.cardEnd)
          );

          const stemCollision = occupiedSpaces.some(
            s => s.side === side && s.level > lvl && s.stemX >= cardStart - 1.5 && s.stemX <= cardEnd + 1.5
          );

          const pierceLower = occupiedSpaces.some(
            s => s.side === side && s.level < lvl && itemX >= s.cardStart - 1.5 && itemX <= s.cardEnd + 1.5
          );

          if (!cardCollision && !stemCollision && !pierceLower) {
            bestLevel = lvl;
            bestAlign = align;
            foundSlot = true;
            break;
          }
        }
        if (foundSlot) break;
      }

      if (!foundSlot) {
        bestLevel = maxLevels - 1;
        bestAlign = itemX > pageWidth / 2 ? 'right' : 'left';
      }

      const cardStart = bestAlign === 'left' ? itemX + 2 : itemX - 2 - cardWidth;
      const cardEnd = cardStart + cardWidth;

      occupiedSpaces.push({
        cardStart,
        cardEnd,
        level: bestLevel,
        side,
        stemX: itemX,
      });

      const branchHeight = 16 + bestLevel * 16;
      const targetY = isTop ? axisY - branchHeight : axisY + branchHeight;
      const cardY = isTop ? targetY - cardHeight : targetY;

      if (isMilestone) {
        doc.setFillColor(245, 158, 11);
        doc.circle(itemX, axisY, 2.2, 'F');
        doc.setDrawColor(255, 255, 255);
        doc.setLineWidth(0.4);
        doc.circle(itemX, axisY, 1.2, 'S');
      } else {
        doc.setFillColor(99, 102, 241);
        doc.circle(itemX, axisY, 1.6, 'F');
      }

      doc.setDrawColor(isMilestone ? 245 : 99, isMilestone ? 158 : 102, isMilestone ? 11 : 241);
      doc.setLineWidth(0.35);
      doc.line(itemX, axisY, itemX, isTop ? targetY : targetY);

      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.25);
      doc.roundedRect(cardStart, cardY, cardWidth, cardHeight, 1.2, 1.2, 'FD');

      doc.setFillColor(isMilestone ? 245 : 99, isMilestone ? 158 : 102, isMilestone ? 11 : 241);
      const accentX = bestAlign === 'left' ? cardStart : cardEnd - 1.2;
      doc.rect(accentX, cardY, 1.2, cardHeight, 'F');

      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      const textX = cardStart + (bestAlign === 'left' ? 3 : 2);
      doc.text(labelText, textX, cardY + 4);

      doc.setFontSize(5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(148, 163, 184);
      doc.text(`${dateStr}  ·  ${typeStr}`, textX, cardY + 7);
    });
  }

  doc.save(`${pName.replace(/\s+/g, '_')}_Roadmap.pdf`);
  addLogEntry('import', `Roadmap-PDF für "${pName}" heruntergeladen`);
}
